import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import KairosLogo from '../../components/KairosLogo';
import './OnboardingPage.css';

export default function OnboardingPage({ onComplete, userName }) {
  const { t } = useTranslation();
  const [step,               setStep]               = useState(1);
  const [preferredPath,      setPreferredPath]      = useState('');
  const [languagePreference, setLanguagePreference] = useState('en');
  const [hometown,           setHometown]           = useState('');

  const PATHS = [
    { id: 'daily',      label: t('onboarding.dailyName'),      icon: '☀', desc: t('onboarding.dailyDesc') },
    { id: 'journey',    label: t('onboarding.journeyName'),    icon: '📖', desc: t('onboarding.journeyDesc') },
    { id: 'choose',     label: t('onboarding.chooseName'),     icon: '🔍', desc: t('onboarding.chooseDesc') },
    { id: 'lectionary', label: t('onboarding.lectionaryName'), icon: '🗓', desc: t('onboarding.lectionaryDesc') },
  ];

  const LANGUAGES = [
    { id: 'en', label: t('onboarding.english'),   icon: '🇬🇧', desc: t('onboarding.englishDesc') },
    { id: 'af', label: t('onboarding.afrikaans'), icon: '🇿🇦', desc: t('onboarding.afrikaansDesc') },
    { id: 'zu', label: t('onboarding.isizulu'),   icon: '🇿🇦', desc: t('onboarding.isizuluDesc') },
  ];

  const totalSteps = 3;

  function next() {
    if (step < totalSteps) setStep(s => s + 1);
    else finish();
  }

  function finish() {
    onComplete({ preferredPath, languagePreference, hometown: hometown.trim() || null });
  }

  const canNext =
    (step === 1 && !!preferredPath)      ||
    (step === 2 && !!languagePreference) ||
    step === 3;

  const greeting = userName
    ? t('onboarding.welcome', { name: userName.split('@')[0] })
    : t('onboarding.welcomeGeneric');

  return (
    <div className="onboard-page">
      <div className="onboard-card">

        {/* Logo + greeting */}
        <div className="onboard-top">
          <KairosLogo size={38} color="#6a7662" />
          <p className="onboard-greeting">{greeting}</p>
        </div>

        {/* Progress dots */}
        <div className="onboard-dots">
          {[1, 2, 3].map(n => (
            <span
              key={n}
              className={`onboard-dot${n === step ? ' active' : n < step ? ' done' : ''}`}
            />
          ))}
        </div>

        {/* ── Step 1: Preferred path ── */}
        {step === 1 && (
          <div className="onboard-step">
            <h2 className="onboard-q">{t('onboarding.whichPath')}</h2>
            <p className="onboard-sub">{t('onboarding.changeLater')}</p>
            <div className="onboard-options">
              {PATHS.map(p => (
                <button
                  key={p.id}
                  type="button"
                  className={`onboard-option${preferredPath === p.id ? ' selected' : ''}`}
                  onClick={() => setPreferredPath(p.id)}
                >
                  <span className="onboard-option-icon">{p.icon}</span>
                  <span className="onboard-option-label">{p.label}</span>
                  <span className="onboard-option-desc">{p.desc}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Step 2: Language ── */}
        {step === 2 && (
          <div className="onboard-step">
            <h2 className="onboard-q">{t('onboarding.whichLanguage')}</h2>
            <p className="onboard-sub">{t('onboarding.languageNote')}</p>
            <div className="onboard-options">
              {LANGUAGES.map(l => (
                <button
                  key={l.id}
                  type="button"
                  className={`onboard-option${languagePreference === l.id ? ' selected' : ''}`}
                  onClick={() => setLanguagePreference(l.id)}
                >
                  <span className="onboard-option-icon">{l.icon}</span>
                  <span className="onboard-option-label">{l.label}</span>
                  <span className="onboard-option-desc">{l.desc}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Step 3: Hometown ── */}
        {step === 3 && (
          <div className="onboard-step">
            <h2 className="onboard-q">{t('onboarding.whereAreYou')}</h2>
            <p className="onboard-sub">{t('onboarding.locationNote')}</p>
            <input
              className="onboard-input"
              type="text"
              placeholder={t('onboarding.locationPlaceholder')}
              value={hometown}
              onChange={e => setHometown(e.target.value)}
              autoFocus
            />
          </div>
        )}

        {/* Navigation */}
        <div className="onboard-footer">
          {step < totalSteps ? (
            <button
              className="onboard-btn-primary"
              onClick={next}
              disabled={!canNext}
              type="button"
            >
              {t('onboarding.next')}
            </button>
          ) : (
            <button
              className="onboard-btn-primary"
              onClick={finish}
              type="button"
            >
              {t('onboarding.enterKairos')}
            </button>
          )}
          {step === 3 && (
            <button className="onboard-skip" onClick={finish} type="button">
              {t('onboarding.skip')}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
